<template>
    <div class="row g-3">
        <!-- NAGŁÓWEK ZAKŁADKI -->
        <div class="col-12 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 border-bottom pb-2 mb-2">
            <div>
                <div class="d-flex align-items-center gap-2">
                    <h6 class="fw-bold mb-0">Zarządzanie Menu</h6>
                    <!-- BADGE NIEZAPISANYCH ZMIAN -->
                    <span v-if="isDirty" class="badge bg-warning text-dark border border-warning">
                        <i class="bi bi-exclamation-triangle-fill me-1"></i>Niezapisane zmiany
                    </span>
                </div>
                <small class="text-muted">Konfiguracja pozycji menu górnego i dolnego z możliwością zmiany
                    kolejności</small>
            </div>
            <button type="button" class="btn btn-primary-dark d-flex align-items-center gap-2"
                :disabled="loading || saving || !isDirty" @click="saveData">
                <i class="bi bi-floppy"></i>
                <span>Zapisz menu</span>
            </button>
        </div>

        <!-- LEWA KOLUMNA: MENU GÓRNE -->
        <div class="col-12 col-lg-6">
            <div class="card shadow-sm border-0 bg-body-tertiary h-100">
                <div
                    class="card-header bg-dark text-white fw-bold d-flex justify-content-between align-items-center py-2">
                    <span><i class="bi bi-layout-three-columns me-2"></i>Menu Górne</span>
                    <span class="badge bg-secondary">{{ form.topMenu.length }} pozycji</span>
                </div>
                <div class="card-body d-flex flex-column justify-content-between p-3">
                    <div class="menu-list">
                        <div v-if="form.topMenu.length === 0" class="text-center text-muted py-4 small">
                            Brak pozycji w menu górnym. Kliknij przycisk poniżej, aby dodać pierwszą.
                        </div>

                        <div v-for="(item, index) in form.topMenu" :key="index" class="card mb-2 border shadow-sm ">
                            <div class="card-body p-2 d-flex align-items-center justify-content-between gap-2">
                                <div class="d-flex align-items-center gap-2 text-truncate">
                                    <span class="badge bg-light text-dark border">{{ index + 1 }}</span>
                                    <div class="text-truncate">
                                        <strong class="d-block text-truncate small">{{ item.title || 'Bez nazwy'
                                            }}</strong>
                                        <small class="text-muted fs-7">Typ: {{ getOptionLabel(item.type) }}</small>
                                    </div>
                                </div>

                                <div class="d-flex align-items-center gap-1 flex-shrink-0">
                                    <!-- PRZESUWANIE W GÓRĘ -->
                                    <button type="button" class="btn btn-sm btn-outline-secondary p-1 px-2"
                                        :disabled="index === 0" title="Przesuń w górę"
                                        @click="moveItem('topMenu', index, -1)">
                                        <i class="bi bi-arrow-up"></i>
                                    </button>
                                    <!-- PRZESUWANIE W DÓŁ -->
                                    <button type="button" class="btn btn-sm btn-outline-secondary p-1 px-2"
                                        :disabled="index === form.topMenu.length - 1" title="Przesuń w dół"
                                        @click="moveItem('topMenu', index, 1)">
                                        <i class="bi bi-arrow-down"></i>
                                    </button>
                                    <!-- EDYCJA -->
                                    <button type="button" class="btn btn-sm btn-outline-primary p-1 px-2" title="Edytuj"
                                        @click="openModal('topMenu', index)">
                                        <i class="bi bi-pencil"></i>
                                    </button>
                                    <!-- USUWANIE -->
                                    <button type="button" class="btn btn-sm btn-outline-danger p-1 px-2" title="Usuń"
                                        @click="removeItem('topMenu', index)">
                                        <i class="bi bi-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button type="button"
                        class="btn btn-outline-dark w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                        @click="openModal('topMenu', null)">
                        <i class="bi bi-plus-circle"></i>
                        <span>Dodaj pozycję do Menu Górnego</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- PRAWA KOLUMNA: MENU DOLNE -->
        <div class="col-12 col-lg-6">
            <div class="card shadow-sm border-0 bg-body-tertiary h-100">
                <div
                    class="card-header bg-dark text-white fw-bold d-flex justify-content-between align-items-center py-2">
                    <span><i class="bi bi-layout-sidebar-reverse me-2"></i>Menu Dolne</span>
                    <span class="badge bg-secondary">{{ form.bottomMenu.length }} pozycji</span>
                </div>
                <div class="card-body d-flex flex-column justify-content-between p-3">
                    <div class="menu-list">
                        <div v-if="form.bottomMenu.length === 0" class="text-center text-muted py-4 small">
                            Brak pozycji w menu dolnym. Kliknij przycisk poniżej, aby dodać pierwszą.
                        </div>

                        <div v-for="(item, index) in form.bottomMenu" :key="index" class="card mb-2 border shadow-sm ">
                            <div class="card-body p-2 d-flex align-items-center justify-content-between gap-2">
                                <div class="d-flex align-items-center gap-2 text-truncate">
                                    <span class="badge bg-light text-dark border">{{ index + 1 }}</span>
                                    <div class="text-truncate">
                                        <strong class="d-block text-truncate small">{{ item.title || 'Bez nazwy'
                                            }}</strong>
                                        <small class="text-muted fs-7">Typ: {{ getOptionLabel(item.type) }}</small>
                                    </div>
                                </div>

                                <div class="d-flex align-items-center gap-1 flex-shrink-0">
                                    <!-- PRZESUWANIE W GÓRĘ -->
                                    <button type="button" class="btn btn-sm btn-outline-secondary p-1 px-2"
                                        :disabled="index === 0" title="Przesuń w górę"
                                        @click="moveItem('bottomMenu', index, -1)">
                                        <i class="bi bi-arrow-up"></i>
                                    </button>
                                    <!-- PRZESUWANIE W DÓŁ -->
                                    <button type="button" class="btn btn-sm btn-outline-secondary p-1 px-2"
                                        :disabled="index === form.bottomMenu.length - 1" title="Przesuń w dół"
                                        @click="moveItem('bottomMenu', index, 1)">
                                        <i class="bi bi-arrow-down"></i>
                                    </button>
                                    <!-- EDYCJA -->
                                    <button type="button" class="btn btn-sm btn-outline-primary p-1 px-2" title="Edytuj"
                                        @click="openModal('bottomMenu', index)">
                                        <i class="bi bi-pencil"></i>
                                    </button>
                                    <!-- USUWANIE -->
                                    <button type="button" class="btn btn-sm btn-outline-danger p-1 px-2" title="Usuń"
                                        @click="removeItem('bottomMenu', index)">
                                        <i class="bi bi-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button type="button"
                        class="btn btn-outline-dark w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                        @click="openModal('bottomMenu', null)">
                        <i class="bi bi-plus-circle"></i>
                        <span>Dodaj pozycję do Menu Dolnego</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- DOLNY PRZYCISK ZAPISU -->
        <div class="col-12 d-flex justify-content-end mt-4 pt-3 border-top">
            <button type="button" class="btn btn-primary-dark d-flex align-items-center gap-2"
                :disabled="loading || saving || !isDirty" @click="saveData">
                <i class="bi bi-floppy"></i>
                <span>Zapisz menu</span>
            </button>
        </div>
    </div>

    <!-- MODAL EDYCJI / DODAWANIA POZYCJI MENU -->
    <div class="modal fade" id="menuItemModal" tabindex="-1" aria-labelledby="menuItemModalLabel" aria-hidden="true"
        ref="modalRef">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title fw-bold" id="menuItemModalLabel">
                        {{ editingIndex !== null ? 'Edycja pozycji menu' : 'Nowa pozycja menu' }}
                        <span class="text-muted fs-6">({{ activeMenuType === 'topMenu' ? 'Menu Górne' : 'Menu Dolne'
                            }})</span>
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body p-3">
                    <!-- NAZWA POZYCJI MENU -->
                    <div class="mb-3">
                        <label class="form-label fw-semibold">Nazwa pozycji w menu</label>
                        <input type="text" class="form-control" v-model="activeItem.title"
                            placeholder="np. O nas, Regulamin, Oferta" />
                    </div>

                    <!-- WYBÓR TYPU ROZWIAZANIA (RADIO) -->
                    <div class="mb-3">
                        <label class="form-label fw-semibold d-block">Typ zawartości / Opcja</label>
                        <div class="d-flex flex-wrap gap-3 p-2 bg-light rounded border">
                            <div class="form-check">
                                <input class="form-check-input" type="radio" name="menuOptionType" id="opcja1"
                                    value="opcja1" v-model="activeItem.type" @change="handleTypeChange" />
                                <label class="form-check-label fw-medium" for="opcja1">Strona WYSIWYG</label>
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="radio" name="menuOptionType" id="opcja2"
                                    value="opcja2" v-model="activeItem.type" @change="handleTypeChange" />
                                <label class="form-check-label fw-medium" for="opcja2">Formularz poszukuje auto</label>
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="radio" name="menuOptionType" id="opcja3"
                                    value="opcja3" v-model="activeItem.type" @change="handleTypeChange" />
                                <label class="form-check-label fw-medium" for="opcja3">Formularz sprzedaj auto</label>
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="radio" name="menuOptionType" id="opcja4"
                                    value="opcja4" v-model="activeItem.type" @change="handleTypeChange" />
                                <label class="form-check-label fw-medium" for="opcja4">Adres i formularz
                                    kontaktowy</label>
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="radio" name="menuOptionType" id="opcja6"
                                    value="opcja6" v-model="activeItem.type" @change="handleTypeChange" />
                                <label class="form-check-label fw-medium" for="opcja6">Wyniki wyszukiwania</label>
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="radio" name="menuOptionType" id="opcja5"
                                    value="opcja5" v-model="activeItem.type" @change="handleTypeChange" />
                                <label class="form-check-label fw-medium" for="opcja5">Własne przekierowanie</label>
                            </div>
                        </div>
                    </div>

                    <!-- WIDOK DLA OPCJI 1 (SUMMERNOTE WYSIWYG) -->
                    <div v-show="activeItem.type === 'opcja1'" class="mb-3">
                        <label class="form-label fw-semibold">Treść podstrony (WYSIWYG)</label>
                        <div ref="editorRef"></div>
                    </div>

                    <!-- WIDOK DLA POZOSTAŁYCH OPCJI (NP. LINK / ADRES) -->
                    <div class="mb-3">
                        <label class="form-label fw-semibold">Adres URL / Odnośnik dla {{
                            getOptionLabel(activeItem.type) }}</label>
                        <input type="text" class="form-control" v-model="activeItem.url"
                            placeholder="np. /kontakt lub https://domena.pl/strona" />
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Anuluj</button>
                    <button type="button" class="btn btn-primary-dark" @click="applyModalChanges">
                        Zapisz pozycję
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- LOADER -->
    <AppLoader :show="loading || saving" :text="loaderText" />
</template>

<script setup>
import { ref, onMounted, nextTick, onBeforeUnmount, watch } from 'vue'
import apiClient from '@/api'
import { onBeforeRouteLeave } from 'vue-router'
import Modal from 'bootstrap/js/dist/modal'
import $ from 'jquery'

// Wpięcie jQuery pod obiekty globalne dla Summernote
window.jQuery = $
window.$ = $

if (!$.now) {
    $.now = () => Date.now()
}

import 'summernote/dist/summernote-lite.min.css'
import 'summernote/dist/summernote-lite.min.js'

import AppLoader from '@/components/AppLoader.vue'
import { webAlert, showConfirm } from '@/utils/dialog'

const isDirty = ref(false)
const isInitialLoading = ref(true)

const loading = ref(false)
const saving = ref(false)
const loaderText = ref('')

const modalRef = ref(null)
const editorRef = ref(null)
let bsModalInstance = null

const activeMenuType = ref('topMenu') // 'topMenu' | 'bottomMenu'
const editingIndex = ref(null)

const activeItem = ref({
    title: '',
    type: 'opcja1',
    content: '',
    url: ''
})

const form = ref({
    topMenu: [],
    bottomMenu: []
})

const getOptionLabel = (type) => {
    switch (type) {
        case '1':
        case 'opcja1':
            return 'Strona WYSIWYG'
        case '2':
        case 'opcja2':
            return 'Formularz poszukuje auto'
        case '3':
        case 'opcja3':
            return 'Formularz sprzedaj auto'
        case '4':
        case 'opcja4':
            return 'Adres i formularz kontaktowy'
        case '5':
        case 'opcja5':
            return 'Własne przekierowanie'
        default:
            return type
    }
}

// Customowy przycisk dla Summernote wstawiający siatkę Bootstrapa
const createBootstrapGridButton = (context) => {
    const ui = $.summernote.ui
    const $button = ui.button({
        contents: '<i class="bi bi-layout-split"></i> 2 Kolumny',
        click: () => {
            const gridHtml = `
                <div class="row my-2 p-2 border bg-light">
                    <div class="col-12 col-md-6 border p-2 bg-white">
                        <p><strong>Lewa kolumna</strong></p>
                        <p>Wpisz treść...</p>
                    </div>
                    <div class="col-12 col-md-6 border p-2 bg-white">
                        <p><strong>Prawa kolumna</strong></p>
                        <p>Wpisz treść...</p>
                    </div>
                </div>
                <p><br></p>
            `
            context.invoke('editor.pasteHTML', gridHtml)
        }
    }).render()

    // Usunięcie atrybutów tooltipów, zapobiega błędowi z TypeError / offset().top
    $button.removeAttr('data-original-title title data-toggle')

    return $button
}

const destroySummernote = () => {
    if (editorRef.value) {
        try {
            $(editorRef.value).summernote('destroy')
        } catch (e) {
            // Ignorujemy jeśli nie był wcześniej zainicjalizowany
        }
    }
}

const initSummernote = () => {
    if (!editorRef.value) return

    destroySummernote()

    $(editorRef.value).summernote({
        height: 250,
        tooltip: false,
        toolbar: [
            ['style', ['bold', 'italic', 'underline', 'clear']],
            ['font', ['strikethrough']],
            ['para', ['ul', 'ol', 'paragraph']],
            ['table', ['table']],
            ['insert', ['link', 'bsGrid']],
            ['view', ['codeview']]
        ],
        buttons: {
            bsGrid: createBootstrapGridButton
        },
        callbacks: {
            onChange: (contents) => {
                activeItem.value.content = contents
            }
        }
    })

    $(editorRef.value).summernote('code', activeItem.value.content || '')
}

// Reagowanie na zmianę typu radio wewnątrz modala
const handleTypeChange = () => {
    if (activeItem.value.type === 'opcja1') {
        nextTick(() => {
            initSummernote()
        })
    } else {
        destroySummernote()
    }
}

// Otwieranie Modala edycji/dodawania
const openModal = (menuType, index) => {
    activeMenuType.value = menuType
    editingIndex.value = index

    if (index !== null) {
        activeItem.value = { ...form.value[menuType][index] }
    } else {
        activeItem.value = {
            title: '',
            type: 'opcja1',
            content: '',
            url: ''
        }
    }

    if (!bsModalInstance && modalRef.value) {
        bsModalInstance = new Modal(modalRef.value)
    }
    bsModalInstance?.show()

    nextTick(() => {
        if (activeItem.value.type === 'opcja1') {
            initSummernote()
        } else {
            destroySummernote()
        }
    })
}

// Zapisywanie zmian z modala do lokalnej tablicy
const applyModalChanges = () => {
    if (!activeItem.value.title.trim()) {
        webAlert('Podaj nazwę pozycji menu.')
        return
    }

    if (activeItem.value.type === 'opcja1' && editorRef.value) {
        activeItem.value.content = $(editorRef.value).summernote('code')
    }

    const list = form.value[activeMenuType.value]

    if (editingIndex.value !== null) {
        list[editingIndex.value] = { ...activeItem.value }
    } else {
        list.push({ ...activeItem.value })
    }

    bsModalInstance?.hide()
}

// Przesuwanie elementów góra/dół na liście
const moveItem = (menuType, index, direction) => {
    const list = form.value[menuType]
    const targetIndex = index + direction

    if (targetIndex < 0 || targetIndex >= list.length) return

    const temp = list[index]
    list[index] = list[targetIndex]
    list[targetIndex] = temp
}

// Usuwanie pozycji z listy
const removeItem = async (menuType, index) => {
    const confirmed = await showConfirm('Czy na pewno chcesz usunąć tę pozycję z menu?', {
        title: 'Potwierdzenie usunięcia',
        confirmText: 'Usuń',
        cancelText: 'Anuluj'
    })

    if (confirmed) {
        form.value[menuType].splice(index, 1)
    }
}

// Pobieranie danych z API
const loadData = async () => {
    loading.value = true
    isInitialLoading.value = true
    loaderText.value = 'Pobieranie struktur menu...'

    try {
        const response = await apiClient.post(
            { action: 'get-settings-menu' }
        )

        if (response.data) {
            form.value.topMenu = Array.isArray(response.data.topMenu) ? response.data.topMenu : []
            form.value.bottomMenu = Array.isArray(response.data.bottomMenu) ? response.data.bottomMenu : []
        } else {
            form.value.topMenu = []
            form.value.bottomMenu = []
        }

        // Czyszczenie flag po załadowaniu z bazy
        await nextTick()
        isDirty.value = false
        isInitialLoading.value = false
    } catch (error) {
        webAlert('Błąd podczas pobierania danych menu.')
    } finally {
        loading.value = false
    }
}

// Zapisywanie danych do API
// Modyfikacja saveData - resetowanie isDirty po udanym zapisie
const saveData = async () => {
    saving.value = true
    loaderText.value = 'Zapisywanie struktur menu...'

    try {
        const response = await apiClient.post(
            {
                action: 'save-settings-menu',
                topMenu: form.value.topMenu,
                bottomMenu: form.value.bottomMenu
            }
        )

        // Resetujemy flagę po pomyślnym zapisie!
        isDirty.value = false

        webAlert(response.data);
    } catch (error) {
        webAlert('Błąd podczas zapisywania struktury menu.')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    loadData()
})

onBeforeUnmount(() => {
    destroySummernote()
})

watch(
    form,
    () => {
        if (!isInitialLoading.value) {
            isDirty.value = true
        }
    },
    { deep: true }
)

// OCHRONA PRZED PRZEJŚCIEM DO INNEJ ZAKŁADKI / PODSTRONY
onBeforeRouteLeave(async (to, from, next) => {
    if (isDirty.value) {
        // Jeśli showConfirm zwraca true dla głównego przycisku:
        const stayAndSave = await showConfirm(
            'Masz niezapisane zmiany w menu. Czy chcesz pozostać na stronie i je zapisać?',
            {
                title: 'Niezapisane zmiany',
                confirmText: 'Zostań i zapisz', // Główny przycisk (po prawej)
                cancelText: 'Opuść bez zapisywania' // Poboczny/Anuluj (po lewej)
            }
        )

        if (stayAndSave) {
            next(false) // Blokujemy nawigację – użytkownik zostaje
        } else {
            next() // Pozwalamy wyjść
        }
    } else {
        next()
    }
})
</script>

<style scoped>
.menu-list {
    min-height: 150px;
}

.fs-7 {
    font-size: 0.8rem;
}
</style>